import { ChangeEvent, forwardRef, useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { css } from "styled-components";

import SendIcon from "../../../assets/icons/send.svg";
import { CheckBox } from "../../../components/atoms/CheckBox";
import { IconButton } from "../../../components/atoms/IconButton";
import { TextArea } from "../../../components/atoms/TextArea";
import { ImagePicker } from "../../../components/molecules/ImagePicker";
import { FilePreview } from "../../../components/organisms/FilePreview";
import useChatRoomId from "../../../hooks/useChatRoomId";
import useLocalStorage from "../../../hooks/useLocalStorage";
import { MessageParams, fields } from "../hooks/useMessageForm";
import { Scrollable } from "../../../hooks/useScroll";
import { MessageType } from "../../../models/message";
import { zIndex } from "../../../styles";
import MoonLoader from "react-spinners/MoonLoader";
import tw from "twin.macro";

interface Props {
  disabled?: boolean;
  placeholder?: string;
  enableQuestion?: boolean;
  enableAnswer?: boolean;
  enableResult?: boolean;
  enableClose?: boolean;
  onSubmit?: (data: MessageParams) => Promise<void>;
}

export const ChatRoomForm = forwardRef<HTMLDivElement, Props>((props, ref) => {
  const [formHeight, setFormHeight] = useState<number | "auto">();

  const form = useFormContext<MessageParams>();
  const { makeCustomKey, keys, setItemAsString, getSerializedItem } =
    useLocalStorage();
  const chatRoomId = useChatRoomId();
  const textarea = useRef<HTMLTextAreaElement>();

  const {
    disabled = false,
    placeholder = "メッセージを作成",
    enableQuestion = false,
    enableAnswer = false,
    enableResult = false,
    onSubmit,
  } = props;

  const key = makeCustomKey(keys.chatRoomInput, chatRoomId?.toString());
  const [fileValue, messageTypeValue] = form.watch([
    fields.file.name,
    fields.messageType.name,
  ]);
  const isFileUploaded = fileValue?.length > 0;
  const isEnableQA = enableQuestion || enableAnswer;
  const QALabel = enableQuestion ? "質問として送る" : "回答として送る";

  useEffect(() => {
    form.setValue("body", getSerializedItem(key));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChangeText = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const { value } = e.target;
    setItemAsString(key, value);

    if (value === "") {
      setFormHeight("auto");
    } else {
      setFormHeight(textarea.current?.scrollHeight);
    }
  };

  const removeFile = () => form.setValue(fields.file.name, undefined);

  const onChangeResultFlag = (e: ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    if (enableResult && checked) {
      form.setValue(fields.messageType.name, MessageType.result);
    } else {
      form.setValue(fields.messageType.name, MessageType.plain);
    }
  };

  const onChangeQAFlag = (e: ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    if (enableQuestion && checked) {
      form.setValue(fields.messageType.name, MessageType.question);
    } else if (enableAnswer && checked) {
      form.setValue(fields.messageType.name, MessageType.answer);
    } else {
      form.setValue(fields.messageType.name, MessageType.plain);
    }
  };

  const handleSubmit = form.handleSubmit(async (data) => {
    setFormHeight("auto");
    await onSubmit(data);
  });

  const isDisabled = disabled || form.formState.isSubmitting;

  return (
    <Scrollable
      name="chatRoomForm"
      tw="fixed bottom-0 w-full max-w-screen-sm"
      css={{ zIndex: zIndex.priority }}
      ref={ref}
    >
      <div tw="relative">
        {isFileUploaded && (
          <FilePreview files={fileValue} onRemove={removeFile} />
        )}

        {form.formState.isSubmitting && (
          <div tw="bg-black bg-opacity-80 w-10 h-10 rounded flex-center absolute -top-14 right-5">
            <MoonLoader size={20} color="#fff" />
          </div>
        )}

        <form
          tw="w-full p-[15px] border-t border-solid border-gray bg-white min-h-[64px]"
          onSubmit={handleSubmit}
        >
          <div tw="flex items-center">
            <ImagePicker name={fields.file.name} disabled={isDisabled} />
            <TextArea
              ref={textarea}
              name={fields.body.name}
              onChange={handleChangeText}
              placeholder={placeholder}
              disabled={isDisabled}
              rows={1}
              tw="rounded-[21px] py-[10px] px-[15px] mx-2 flex-1 text-sm leading-5"
              css={css({
                height: formHeight,
                maxHeight: 240,
              })}
            />
            <IconButton
              type="submit"
              icon={
                <SendIcon
                  tw="svg text-theme"
                  css={isDisabled && tw`text-opacity-60`}
                />
              }
              disabled={isDisabled || !onSubmit}
            />
          </div>
          {/** renderFormPartsとかでpropsから渡すようにできれば良さそう */}
          {(isEnableQA || enableResult) && (
            <div tw="mt-2">
              {isEnableQA && (
                <div tw="mr-2 inline-block">
                  <CheckBox
                    name="QA"
                    label={QALabel}
                    size="small"
                    onChange={onChangeQAFlag}
                    checked={
                      messageTypeValue === MessageType.question ||
                      messageTypeValue === MessageType.answer
                    }
                    disabled={isDisabled}
                    tw="svg"
                  />
                </div>
              )}
              {enableResult && (
                <CheckBox
                  name="isResult"
                  label="鑑定結果として送る"
                  size="small"
                  onChange={onChangeResultFlag}
                  checked={messageTypeValue === MessageType.result}
                  defaultChecked={false}
                  disabled={isDisabled}
                  tw="svg"
                />
              )}
            </div>
          )}
        </form>
      </div>
    </Scrollable>
  );
});

ChatRoomForm.displayName = "ChatRoomForm";
